// talkingAvatarInsert — puts a finished talking-avatar job on its scene.
//
// One implementation for every apply path (the Avatar tab's "Sahneye Ekle",
// the job widget's Approve, the Activity history "add"):
//   1. the scene's narration sound is REPLACED by the job's converted voice
//      (result.audioUrl) — or a new narration is added when there is none;
//   2. the video goes in as an EnrichedPlayer box, MUTED (state.volume 0), so
//      the scene plays exactly one voice: the narration;
//   3. both start at the same second: the replaced narration's own start, else
//      the playhead (when it is in that scene), else 0.
//
// The target scene is ALWAYS the job's own scene (job.navId / result.navId) —
// never "whatever is open now". If that scene was deleted meanwhile, the result
// is ADDED to the current scene; nothing is replaced there.
//
// Mechanics that matter:
//   • EDIT_SOUND swaps the whole entry, so the replacement spreads the stored
//     one (id, trackId, rowOrder, volumeLevel, name survive) and drops the
//     split offset; the other pieces of a split narration are deleted.
//   • addSound / addBoxThunk move an unpositioned item to the playhead, so the
//     sound gets an explicit startTime and the box an explicit timeline.
//   • Heavy editor modules are required lazily in defaultDeps() — tests inject
//     fakes, and importing this file stays cheap for the async widget.
import { parseTimeStr, FPS } from "../../../../common/sceneTimeline.es6";
import { getStoredTalkingAvatarJob, markTalkingAvatarJobApplied } from "./talkingAvatarJobStore";
import { VOICE_PROVIDER_AZURE, voiceProviderOf } from "./talkingAvatarVoice";

export function isTalkingAvatarJob(job) {
  if (!job || typeof job !== "object") return false;
  const type = String(job.type || job.jobType || "")
    .toLowerCase()
    .replace(/_/g, "-");
  return type === "talking-avatar" || !!(job.result && job.result.kind === "talking-avatar");
}

const isLabelled = (sound) =>
  !!sound.voiceData || (typeof sound.narrationText === "string" && !!sound.narrationText.trim());

const earliest = (list) =>
  list.reduce(
    (best, c) =>
      best === null || parseTimeStr(c.sound.startTime) < parseTimeStr(best.sound.startTime)
        ? c
        : best,
    null
  );

// The scene's narration: the earliest sound with voiceData or narration text
// (loopScene background music never counts). An unlabelled sound is never
// taken for the narration — it may be music or an effect, and the tray /
// history apply paths offer no target choice — so without a labelled sound
// this is null and the caller ADDS a new sound. (The Avatar tab's target
// picker can still replace any sound explicitly.)
export function pickNarrationSound(soundList) {
  const list = Array.isArray(soundList) ? soundList : [];
  const labelled = [];
  list.forEach((sound, index) => {
    if (sound && !sound.loopScene && isLabelled(sound)) labelled.push({ sound, index });
  });
  return labelled.length ? earliest(labelled) : null;
}

function findSound(list, key) {
  if (typeof key === "number") return list[key] ? { sound: list[key], index: key } : null;
  const index = list.findIndex((s) => s && s.id === key);
  return index >= 0 ? { sound: list[index], index } : null;
}

// voice: { id, name, isPrivate, provider } — id is what voiceData.voiceId
// stores (the ElevenLabs id for stock voices, the DB id for an org's cloned
// voice — the same convention AIVoiceTab uses for "own" narrations; the SSML
// voice name for Azure).
//
// voiceData mirrors what AIVoiceTab's buildNarrationSoundData writes, so the
// AI voice tab reopens the narration on the right narrator: provider is the
// UI name ("elevenlabs" / "azure" — the API calls Azure "microsoft"), and
// language is the voice list it is found in — "multilingual" for ElevenLabs
// and for the Azure conversion voices (all multilingual, listed under
// "multilingual" in api/course/voices).
function talkingVoiceData(voice) {
  const isPrivate = !!(voice && voice.isPrivate);
  const isAzure = !isPrivate && voiceProviderOf(voice) === VOICE_PROVIDER_AZURE;
  return {
    provider: isPrivate ? "private" : isAzure ? "azure" : "elevenlabs",
    narratorMode: isPrivate ? "own" : "ai",
    language: "multilingual",
    voiceId: voice && voice.id != null ? voice.id : "",
    voiceName: (voice && voice.name) || "",
    source: "talking-avatar",
  };
}

export function buildTalkingAvatarSoundData({
  existing,
  result,
  voice,
  startSec,
  sceneIndex,
  text,
}) {
  const base = existing ? { ...existing } : {};
  delete base.audioOffset;
  delete base.splitGroup;
  const start = Number(startSec) || 0;
  const duration = Number(result && result.duration) || 0;
  const data = {
    ...base,
    file: result.audioUrl,
    name:
      base.name ||
      (sceneIndex ? `Scene-${sceneIndex}-narration.mp3` : "talking-avatar-narration.mp3"),
    startTime: start,
    endTime: duration > 0 ? start + duration : undefined,
    volumeLevel: base.volumeLevel != null ? base.volumeLevel : 0.56,
    narrationText: (result && result.narrationText) || text || base.narrationText || "",
    voiceData: talkingVoiceData(voice),
  };
  if (data.endTime === undefined) delete data.endTime;
  return data;
}

const usableResult = (r) =>
  r && typeof r === "object" && r.audioUrl && (r.videoUrl || r.url) ? r : null;

function fallbackNavId(present) {
  const navItems = present.navItemsById || {};
  const sel = present.navItemSelected;
  if (sel && sel !== 0 && navItems[sel]) return sel;
  const ids = Array.isArray(present.navItemsIds) ? present.navItemsIds : Object.keys(navItems);
  return ids.find((id) => id !== 0 && id !== "0" && navItems[id]) || null;
}

function playheadSecFor(playhead, navId) {
  if (!playhead || !(playhead.frame > 0)) return 0;
  return String(playhead.sceneId) === String(navId) ? playhead.frame / FPS : 0;
}

export async function applyTalkingAvatarResult(
  { jobId, navId, result, voice, text, targetSoundId, sceneIndex },
  deps = defaultDeps()
) {
  const res = usableResult(result);
  const store = deps.getStore();
  if (!res || !store) return { ok: false, reason: res ? "noStore" : "noResult" };
  const videoUrl = res.videoUrl || res.url;
  const readPresent = () => (store.getState().undoGroup || {}).present || {};
  const sceneFor = (present) => {
    const items = present.navItemsById || {};
    const exists = !!(navId && items[navId]);
    return { items, exists, target: exists ? navId : fallbackNavId(present) };
  };
  // Nothing visible starts without a scene to put the result on: the spinner
  // has no handle to stop it early, it only times out.
  if (!sceneFor(readPresent()).target) return { ok: false, reason: "noScene" };

  // Geometry first (it awaits the media), then one synchronous burst of
  // dispatches against fresh state.
  const position = {
    x: deps.randomPosition(10, 30),
    y: deps.randomPosition(10, 30),
    type: "absolute",
  };
  const boxId = deps.newBoxId();
  deps.spin({ boxId, x: position.x, y: position.y });
  const size = (await deps.getMediaBoxSize(videoUrl, "video")) || {};

  const present = readPresent();
  const { items: navItems, exists: sceneExists, target: targetNavId } = sceneFor(present);
  // Every scene deleted during the media probe: the spinner times out alone.
  if (!targetNavId) return { ok: false, reason: "noScene" };
  const navItem = navItems[targetNavId];
  const sounds = (present.soundList && present.soundList[targetNavId]) || [];

  let target = null;
  if (sceneExists && targetSoundId !== "new") {
    target =
      (targetSoundId != null && findSound(sounds, targetSoundId)) || pickNarrationSound(sounds);
  }
  const startSec = target
    ? parseTimeStr(target.sound.startTime)
    : playheadSecFor(deps.getPlayhead(), targetNavId);

  const siblings = target
    ? sounds
        .map((sound, index) => ({ sound, index }))
        .filter(
          ({ sound, index }) =>
            index !== target.index &&
            sound &&
            !sound.loopScene &&
            sound.file &&
            sound.file === target.sound.file
        )
        .sort((a, b) => b.index - a.index)
    : [];
  const soundData = buildTalkingAvatarSoundData({
    existing: target && target.sound,
    result: res,
    voice,
    startSec,
    sceneIndex,
    text,
  });

  const inASlide = !!deps.isSlide(navItem.type);
  const duration = Number(res.duration) || 0;
  const ids = {
    parent: inASlide ? navItem.id || targetNavId : navItem.boxes && navItem.boxes[0],
    container: 0,
    name: "EnrichedPlayer",
    position,
    id: boxId,
    page: navItem.id || targetNavId,
    url: videoUrl,
    width: size.width,
    height: size.height,
    widthUnit: size.widthUnit,
    heightUnit: size.heightUnit,
    // Muted: the scene's narration IS this video's voice.
    initialState: { volume: 0 },
    timeline: {
      startFrame: Math.round(startSec * FPS),
      ...(duration > 0 ? { durationHintFrames: Math.round(duration * FPS) } : {}),
    },
  };

  const { dispatch } = store;
  deps.withUndoGroup(`talking-avatar-${jobId || boxId}-${Date.now()}`, () => {
    if (target) {
      // Later pieces go first so an id-less (legacy) narration keeps its index.
      siblings.forEach(({ sound, index }) =>
        dispatch(deps.actions.deleteSound(targetNavId, sound.id != null ? sound.id : index))
      );
      const shift = siblings.filter((s) => s.index < target.index).length;
      const key = target.sound.id != null ? target.sound.id : target.index - shift;
      dispatch(deps.actions.editSound(targetNavId, key, soundData));
    } else {
      dispatch(deps.actions.addSound(targetNavId, soundData));
    }
    deps.createBox(ids, "EnrichedPlayer", inASlide, deps.addBox, present.boxesById || {});
  });

  return {
    ok: true,
    navId: targetNavId,
    boxId,
    replaced: !!target,
    sceneMissing: !sceneExists,
    startSec,
  };
}

// Voice for voiceData: the studio's own record knows the DB id of an org voice;
// without it (another tab, after 48 h) the result's provider id is used. A
// record from before Azure existed has no provider: ElevenLabs.
function voiceForSound(record, result) {
  const rec = record && record.voice;
  if (rec) {
    return {
      id: rec.isPrivate ? (rec.id != null ? rec.id : rec.providerId) : rec.providerId || rec.id,
      name: rec.name || "",
      isPrivate: !!rec.isPrivate,
      provider: voiceProviderOf(rec),
    };
  }
  const vd = (result && result.voiceData) || {};
  return {
    id: vd.voiceId != null ? vd.voiceId : "",
    name: vd.voiceName || "",
    isPrivate: !!vd.isPrivate,
    provider: voiceProviderOf(vd),
  };
}

const appliedJobs = new Set();
const inFlightJobs = new Set();

// job: a widget job row, an Activity-history row, a socket completion or a
// store record.
// opts: { approve, force, onNavigate, targetSoundId, clearDraft }
//   approve    — also POST /jobs/:id/approve (and the AI-avatar job the avatar
//                came from); the widget approves on its own, so it passes false.
//   force      — re-apply an already applied job (history recall).
//   clearDraft — drop the scene's unsaved AI narration draft, but only when the
//                result really REPLACED that scene's narration (never on "new
//                sound", nor in the deleted-scene fallback).
//
// The result comes from the row itself first (job.result, or the history row's
// metadata.result): GET api/talking-avatar/:id answers the job's OWNER only, so
// another org member re-adding it from the history must not depend on it.
export async function applyTalkingAvatarFromJob(job, opts = {}, deps = defaultDeps()) {
  const { approve = false, force = false, onNavigate, targetSoundId, clearDraft = true } = opts;
  const jobId = job && (job.id || job.jobId);
  if (!jobId) return { ok: false, reason: "noJob" };
  const record = deps.getRecord(jobId);
  if (!force && (appliedJobs.has(jobId) || (record && record.applied))) {
    return { ok: false, reason: "duplicate" };
  }
  if (inFlightJobs.has(jobId)) return { ok: false, reason: "inFlight" };
  inFlightJobs.add(jobId);
  try {
    let result =
      usableResult(job.result) ||
      usableResult(job.metadata && job.metadata.result) ||
      usableResult(record && record.result);
    if (!result) {
      const fetched = await deps.getJob(jobId);
      result = usableResult(fetched && fetched.result);
      if (!result) return { ok: false, reason: "noResult" };
    }
    const store = deps.getStore();
    const before = store && store.getState().undoGroup && store.getState().undoGroup.present;
    const openNavId = before && before.navItemSelected;
    const navId =
      job.navId ||
      (record && record.navId) ||
      result.navId ||
      (job.metadata && job.metadata.navId) ||
      null;

    const out = await applyTalkingAvatarResult(
      {
        jobId,
        navId,
        result,
        voice: voiceForSound(record, result),
        text: record && record.text,
        targetSoundId,
        sceneIndex: job.sceneIndex || (record && record.sceneIndex),
      },
      deps
    );
    if (!out.ok) return out;

    appliedJobs.add(jobId);
    deps.markApplied(jobId);
    if (clearDraft && out.replaced && !out.sceneMissing) deps.clearNarrationDraft(out.navId);
    // A "Test üretim" job exists only here: nothing to approve on the API.
    if (approve && !(record && record.test)) {
      deps.approveJob(jobId);
      if (record && record.aiJobId) deps.approveJob(record.aiJobId);
    }
    if (typeof onNavigate === "function" && out.navId && out.navId !== openNavId) {
      deps.defer(() => onNavigate(out.navId));
    }
    return out;
  } catch (error) {
    // Widget / history call this fire-and-forget: report, never reject.
    console.error("[TalkingAvatar] apply failed", jobId, error);
    return { ok: false, reason: "error", error };
  } finally {
    inFlightJobs.delete(jobId);
  }
}

function defaultDeps() {
  /* eslint-disable global-require */
  const { getStore } = require("../../../../common/environment_config.es6");
  const actions = require("../../../../common/actions");
  const { createBox } = require("../../../../common/common_tools");
  const { isSlide } = require("../../../../common/utils");
  const { ID_PREFIX_BOX } = require("../../../../common/constants");
  const { getEditorPlayhead } = require("../../../../common/playhead_tracker.es6");
  const { approveJob } = require("../../../../common/server_utils.es6");
  const { randomPositionGenerator } = require("../../clipboard/clipboard.utils");
  const getMediaBoxSize = require("../../../utils/getMediaBoxSize").default;
  const { spinForSceneAdd } = require("../../../utils/sceneAddSpinner");
  const { clearNarrationDraft } = require("../narrationDraftStore");
  const { getTalkingAvatarJob } = require("./talkingAvatarApi");
  /* eslint-enable global-require */
  return {
    getStore,
    actions: {
      addSound: actions.addSound,
      editSound: actions.editSound,
      deleteSound: actions.deleteSound,
    },
    createBox,
    // EditorApp.handleBoxAdded does exactly this.
    addBox: (...args) => getStore().dispatch(actions.addBoxThunk(...args)),
    isSlide,
    newBoxId: () => ID_PREFIX_BOX + Date.now(),
    randomPosition: randomPositionGenerator,
    getPlayhead: getEditorPlayhead,
    getMediaBoxSize,
    spin: spinForSceneAdd,
    approveJob,
    clearNarrationDraft,
    getJob: (jobId) => getTalkingAvatarJob(jobId),
    getRecord: getStoredTalkingAvatarJob,
    markApplied: markTalkingAvatarJobApplied,
    // One Ctrl+Z reverts the whole apply (sound swap + split cleanup + video
    // box): redux-undo groups consecutive actions with the same groupBy key,
    // and reducers.jsx reads that key from window.cropUndoGroup.
    withUndoGroup: (key, fn) => {
      const prev = window.cropUndoGroup;
      window.cropUndoGroup = key;
      try {
        return fn();
      } finally {
        window.cropUndoGroup = prev || null;
      }
    },
    defer: (fn) => setTimeout(fn, 100),
  };
}

// Test helper.
export function __resetTalkingAvatarInsert() {
  appliedJobs.clear();
  inFlightJobs.clear();
}
